import React, {useEffect, useLayoutEffect, useState} from 'react';
import clsx from 'clsx';
import ResizeDetector from 'react-resize-detector';
import ResizeObserver from 'resize-observer-polyfill';
import {type CSSProperties} from '@mui/styles/withStyles';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import useIntersectionObserver from '@netx/core-components/hooks/useIntersectionObserver';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import {composeClasses, styled} from '@netx/core-components/Theme';
import {
	getScrollBorderUtilityClass,
	scrollBorderClasses,
	type ScrollBorderClasses,
} from './scrollBorderClasses';

const useUtilityClasses = (ownerState: any) => {
	const {
		disableHorizontalScroll,
		hasOverflow,
		hasHorizontalOverflow,
		scrolled,
		scrolledHorizontally,
		scrolledToEnd,
		scrolledToEndHorizontally,
		classes,
	} = ownerState;
	const slots = {
		root: [
			'root',
			disableHorizontalScroll && 'disableHorizontalScroll',
			hasOverflow && 'hasOverflow',
			hasHorizontalOverflow && 'hasHorizontalOverflow',
			scrolled && 'scrolled',
			scrolledToEnd && 'scrolledToEnd',
			scrolledToEndHorizontally && 'scrolledToEndHorizontally',
			scrolledHorizontally && 'scrolledHorizontally',
		],
		scrollBorderBottom: ['scrollBorder', 'scrollBorderBottom'],
		scrollBorderLeft: ['scrollBorder', 'scrollBorderLeft'],
		scrollBorderRight: ['scrollBorder', 'scrollBorderRight'],
		scrollBorderTop: ['scrollBorder', 'scrollBorderTop'],
		scrollWrapper: ['scrollWrapper'],
		scrollWrapperInner: ['scrollWrapperInner'],
		scrollWrapperContent: ['scrollWrapperContent'],
		scrollSentinel: ['scrollSentinel'],
	};
	return composeClasses(
		slots,
		getScrollBorderUtilityClass,
		classes as typeof scrollBorderClasses,
	);
};

const ScrollBorderRoot = styled('div', {
	name: 'ScrollBorder',
	slot: 'Root',
})(({theme}) => ({
	...theme.mixins.flexFillColumn,
	position: 'relative',
	width: '100%',
	[`&:not(.${scrollBorderClasses.scrolled})`]: {
		[`& .${scrollBorderClasses.scrollBorderTop}`]: {
			display: 'none',
		},
	},
	[`&.${scrollBorderClasses.disableHorizontalScroll}`]: {
		[`& .${scrollBorderClasses.scrollWrapper}`]: {
			overflowX: 'hidden',
		},
	},
	[`&:not(.${scrollBorderClasses.hasOverflow}), &.${scrollBorderClasses.scrolledToEnd}`]:
		{
			[`& .${scrollBorderClasses.scrollBorderBottom}`]: {
				display: 'none',
			},
		},

	[`&:not(.${scrollBorderClasses.scrolledHorizontally})`]: {
		[`& .${scrollBorderClasses.scrollBorderLeft}`]: {
			display: 'none',
		},
	},
	[`&:not(.${scrollBorderClasses.hasHorizontalOverflow}), &.${scrollBorderClasses.scrolledToEndHorizontally}`]:
		{
			[`& .${scrollBorderClasses.scrollBorderRight}`]: {
				display: 'none',
			},
		},
}));

const ScrollBorderScrollWrapper = styled('div', {
	name: 'ScrollBorder',
	slot: 'ScrollWrapper',
})(({theme}) => ({
	position: 'relative',
	...theme.mixins.scrollable,
	display: 'flex',
	flexDirection: 'column',
}));

const ScrollBorderScrollWrapperInner = styled('div', {
	name: 'ScrollBorder',
	slot: 'ScrollWrapperInner',
})(({theme}) => ({
	flex: '0 0 auto',
	position: 'relative',
	display: 'flex',
	minWidth: '100%',
}));

const ScrollBorderScrollWrapperContent = styled('div', {
	name: 'ScrollBorder',
	slot: 'ScrollWrapperContent',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	minWidth: '100%',
}));

const ScrollSentinel = styled('div', {
	name: 'ScrollSentinel',
	slot: 'Root',
})(({theme}) => ({
	flex: '0 0 auto',
}));

const ScrollBorderItem = styled('div')(({theme}) => ({
	position: 'absolute',
	pointerEvents: 'none',
	zIndex: 99,
}));

const ScrollBorderBottom = styled(ScrollBorderItem, {
	name: 'ScrollBorder',
	slot: 'ScrollBorderBottom',
})(({theme}) => ({
	borderBottom: `1px solid ${theme.palette.custom.border}`,
	bottom: 0,
	width: '100%',
	height: 25,
}));

const ScrollBorderTop = styled(ScrollBorderItem, {
	name: 'ScrollBorder',
	slot: 'ScrollBorderTop',
})(({theme}) => ({
	borderTop: `1px solid ${theme.palette.custom.border}`,
	top: 0,
	width: '100%',
	height: 25,
}));

const ScrollBorderLeft = styled(ScrollBorderItem, {
	name: 'ScrollBorder',
	slot: 'ScrollBorderLeft',
})(({theme}) => ({
	borderLeft: `1px solid ${theme.palette.custom.border}`,
	left: 0,
	height: '100%',
	width: 25,
}));

const ScrollBorderRight = styled(ScrollBorderItem, {
	name: 'ScrollBorder',
	slot: 'ScrollBorderRight',
})(({theme}) => ({
	borderRight: `1px solid ${theme.palette.custom.border}`,
	right: 0,
	height: '100%',
	width: 25,
}));

export interface ScrollBorderProps
	extends Omit<
		React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLElement>,
		'children'
	> {
	classes?: Partial<ScrollBorderClasses>;
	children?:
		| React.ReactNode
		| ((props: {
				scrolled: boolean;
				scrolledHorizontally: boolean;
				scrolledToEnd: boolean;
				scrolledToEndHorizontally: boolean;
				hasOverflow: boolean;
				hasHorizontalOverflow: boolean;
		  }) => React.ReactNode);
	ref?: React.ForwardedRef<HTMLDivElement>;
	disableHorizontalScroll?: boolean;
	/**
	 * Limit the width of the content to the width of the container
	 *
	 * Note: this must be set to false if you want to utilize the horizontal right side scroll border
	 * @default true
	 */
	limitContents?: boolean;
	/**
	 * `ref` to the scrollable container
	 */
	innerRef?: React.ForwardedRef<HTMLDivElement>;
	contentPadding?: CSSProperties['padding'];
	/**
	 * Hides the scroll borders
	 *
	 * Useful for when you want to stylize yourself via the css classes
	 */
	hide?: boolean;
	onOverflowChange?: (overflown: boolean) => void;
	ScrollWrapperProps?: Partial<
		React.ComponentProps<typeof ScrollBorderScrollWrapper>
	>;
	ScrollWrapperInnerProps?: Partial<
		React.ComponentProps<typeof ScrollBorderScrollWrapperInner>
	>;
}

const ScrollBorderPrimitive = React.forwardRef<
	HTMLDivElement,
	ScrollBorderProps
>(
	(
		{
			disableHorizontalScroll,
			limitContents = true,
			onOverflowChange,
			ScrollWrapperProps,
			ScrollWrapperInnerProps,
			...props
		},
		ref,
	) => {
		const [scrolled, setScrolled] = useState(false);
		const [scrolledHorizontally, setScrolledHorizontally] = useState(false);
		const [scrolledToEnd, setScrolledToEnd] = useState(false);
		const [scrolledToEndHorizontally, setScrolledToEndHorizontally] =
			useState(false);
		const [hasOverflow, setHasOverflow] = useState(false);
		const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

		const wrapperRef = React.useRef<HTMLDivElement | null>(null);

		const {
			classes: _classes,
			children,
			className,
			contentPadding,
			hide,
			innerRef,
			...sendProps
		} = props;

		const internalRef = useSharedRef<HTMLDivElement | null>(null, [innerRef]);

		const classes = useUtilityClasses({
			classes: _classes,
			disableHorizontalScroll,
			scrolled,
			scrolledHorizontally,
			scrolledToEnd,
			scrolledToEndHorizontally,
			hasOverflow,
			hasHorizontalOverflow,
		});

		const notifyOverflowChange = useConstCallback(() =>
			onOverflowChange?.(hasOverflow),
		);

		useEffect(() => {
			notifyOverflowChange();
		}, [hasOverflow]);

		const intersectionObserver = useIntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const end = entry.target.getAttribute('data-id');
					if (end === 'top') {
						setScrolled(!entry.isIntersecting);
					} else if (end === 'bottom') {
						setScrolledToEnd(entry.isIntersecting);
					} else if (end === 'left') {
						setScrolledHorizontally(!entry.isIntersecting);
					} else if (end === 'right') {
						setScrolledToEndHorizontally(entry.isIntersecting);
					}
				});
			},
			{
				threshold: [1, 0],
			},
		);

		// Resize listener
		useLayoutEffect(() => {
			if (!internalRef.current || !wrapperRef.current) return;

			const RO = new ResizeObserver(() => {
				// console.log('ResizeObserver', internalRef.current);
				if (!internalRef.current) return;
				setHasOverflow(
					internalRef.current.clientHeight < internalRef.current.scrollHeight,
				);
				setHasHorizontalOverflow(
					internalRef.current.clientWidth < internalRef.current.scrollWidth,
				);
			});
			RO.observe(internalRef.current);
			RO.observe(wrapperRef.current);

			return () => {
				RO.disconnect();
			};
		}, [internalRef, wrapperRef]);

		// useLayoutEffect(() => {
		// 	if (!wrapperRef.current) return;

		// 	const RO = new ResizeObserver(() => {
		// 		if (!internalRef.current) return;
		// 		setHasOverflow(
		// 			internalRef.current.clientHeight < internalRef.current.scrollHeight,
		// 		);
		// 		setHasHorizontalOverflow(
		// 			internalRef.current.clientWidth < internalRef.current.scrollWidth,
		// 		);
		// 	});
		// 	RO.observe(wrapperRef.current);

		// 	return () => {
		// 		RO.disconnect();
		// 	};
		// }, [wrapperRef]);

		return (
			<ScrollBorderRoot
				ref={ref}
				className={clsx(classes.root, className)}
				{...sendProps}
			>
				{!hide && <ScrollBorderTop className={classes.scrollBorderTop} />}
				{!hide && <ScrollBorderLeft className={classes.scrollBorderLeft} />}
				<ScrollBorderScrollWrapper
					className={clsx(classes.scrollWrapper, className)}
					style={{padding: contentPadding}}
					ref={internalRef}
					tabIndex={-1}
					{...ScrollWrapperProps}
				>
					<ScrollBorderScrollWrapperInner
						className={classes.scrollWrapperInner}
						{...ScrollWrapperInnerProps}
						style={{
							width: limitContents ? '100%' : undefined,
							...ScrollWrapperInnerProps?.style,
						}}
					>
						<ScrollSentinel
							className={classes.scrollSentinel}
							ref={(node) => intersectionObserver.registerRef(2, node)}
							data-id="left"
							style={{height: '100%', position: 'sticky', top: 0}}
						/>

						<ScrollBorderScrollWrapperContent
							ref={wrapperRef}
							className={classes.scrollWrapperContent}
							style={{
								flex: !limitContents ? '0 0 auto' : undefined,
							}}
						>
							<ScrollSentinel
								className={classes.scrollSentinel}
								ref={(node) => intersectionObserver.registerRef(0, node)}
								data-id="top"
								style={{
									width: '100%',
								}}
							/>
							{typeof children === 'function'
								? children({
										scrolled,
										scrolledHorizontally,
										scrolledToEnd,
										scrolledToEndHorizontally,
										hasOverflow,
										hasHorizontalOverflow,
								  })
								: children}
							<ScrollSentinel
								className={classes.scrollSentinel}
								ref={(node) => intersectionObserver.registerRef(1, node)}
								data-id="bottom"
								style={{width: '100%', height: 1}}
							/>
						</ScrollBorderScrollWrapperContent>

						{hasHorizontalOverflow ? (
							<ScrollSentinel
								className={classes.scrollSentinel}
								ref={(node) => intersectionObserver.registerRef(3, node)}
								data-id="right"
								style={{
									height: '100%',
								}}
							/>
						) : null}
					</ScrollBorderScrollWrapperInner>
				</ScrollBorderScrollWrapper>
				{!hide && <ScrollBorderRight className={classes.scrollBorderRight} />}
				{!hide && <ScrollBorderBottom className={classes.scrollBorderBottom} />}
			</ScrollBorderRoot>
		);
	},
);

export const ScrollBorder = ScrollBorderPrimitive;
export default ScrollBorder;
